import type { ReactNode } from 'react'
import { Sparkline } from './Sparkline'

type Tone = 'default' | 'profit' | 'risk' | 'time'

export interface KpiCardProps {
  label: string
  value: ReactNode
  icon?: ReactNode
  tone?: Tone
  trend?: { dir: 'up' | 'down' | 'flat'; label: string }
  meta?: string
  spark?: number[]
  sparkColor?: string
  /** Optional progress bar (0..1). Renders below the value. */
  progress?: { value: number; track?: string; fill?: string; meta?: ReactNode }
}

const TONE_CLASS: Record<Tone, string> = {
  default: '',
  profit:  ' kpi-card--profit',
  risk:    ' kpi-card--risk',
  time:    ' kpi-card--time',
}

export function KpiCard(props: KpiCardProps) {
  const tone = props.tone ?? 'default'
  return (
    <article className={'kpi-card' + TONE_CLASS[tone]} style={{ position: 'relative' }}>
      <header className="kpi-card__header">
        <h2 className="kpi-card__label">{props.label}</h2>
        {props.icon && <span className="kpi-card__icon-wrap" aria-hidden="true">{props.icon}</span>}
      </header>

      <div className="kpi-card__value">
        {props.value}
        {props.trend && (
          <span className={'kpi-card__delta kpi-card__delta--' + (props.trend.dir === 'up' ? 'up' : props.trend.dir === 'down' ? 'down' : 'flat')}>
            {props.trend.dir === 'up' ? '▲' : props.trend.dir === 'down' ? '▼' : '•'} {props.trend.label}
          </span>
        )}
      </div>

      {props.meta && <p className="kpi-card__detail">{props.meta}</p>}

      {props.progress && (
        <div className="kpi-card__progress">
          <div className="kpi-card__progress-track">
            <div
              className="kpi-card__progress-fill"
              style={{ width: Math.max(0, Math.min(100, props.progress.value * 100)).toFixed(1) + '%' }}
            />
          </div>
          {props.progress.meta && <div className="kpi-card__progress-meta">{props.progress.meta}</div>}
        </div>
      )}

      {props.spark && props.spark.length > 1 && (
        <div style={{ position: 'absolute', bottom: 0, right: 0, height: 38, width: 90, opacity: 0.9 }}>
          <Sparkline values={props.spark} color={props.sparkColor ?? 'var(--color-primary)'} />
        </div>
      )}
    </article>
  )
}
